import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Tabs,
  Select,
  Image,
  Flex,
  Title,
  Box,
  Text,
  useMantineTheme,
} from "@mantine/core";

import "@mantine/core/styles.css";

import { userFoundData } from "../utils/apis";

import { URLs } from "../utils/apis";
import { decodeHTML } from "../utils/decodeHTML";

//select icon
import { IconCaretDownFilled } from "@tabler/icons-react";

import Loading from "./Loading";
import { formatCollectionUserFound } from "../utils/formatCollectionUserFound";

import DownloadCSV from "./DownloadCSV";
import { formatHexStr } from "../utils/formatHexStr";
import { useStylesH1 } from "../utils/mantine/useStylesH1";
import { useStylesContentBlocks } from "../utils/mantine/useStylesContentBlocks";
import { useStylesTabsTable } from "../utils/mantine/useStylesTabsTable";
import {
  stampHistoryTableColumns,
  stampHistoryTableOptions,
  stampHistoryTableData,
} from "../utils/userFound/stampHistoryTable";
import {
  collectionsTableColumns,
  collectionsTableOptions,
  collectionsTableData,
} from "../utils/userFound/collectionsTable";
import {
  collectionTableColumns,
  collectionTableOptions,
  collectionTableData,
} from "../utils/userFound/collectionTable";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { useTableIcons } from "../utils/collectionFound/iconsUtil";
import { useStylesTable } from "../utils/mantine/useStylesTable";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import {
  formatTimeStamp,
  SHORT_DISPLAY_FORMAT,
} from "../utils/collectionFound/dateTimeUtils";
import { useStylesUserContent } from "../utils/mantine/useStylesUserContent";
import { useStylesIcons } from "../utils/mantine/useStylesIcons";
import { useStylesUserFound } from "../utils/mantine/useStylesUserFound";

const optionsFilter = ({ options, search }) => {
  const searchLower = search.toLowerCase().trim();
  return options.filter((option) =>
    option.value.toLowerCase().includes(searchLower),
  );
};

const UserFoundContent = ({ staticBasePath }) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  // css classes
  const { classes: h1Classes } = useStylesH1();
  const { classes: contentBlocksClasses } = useStylesContentBlocks();
  const { classes: tabsTableClasses } = useStylesTabsTable();
  const { classes: tableClasses } = useStylesTable();
  const { classes: userContentClasses } = useStylesUserContent();
  const { classes: iconsClasses } = useStylesIcons();
  const { classes: userFoundClasses } = useStylesUserFound();

  const [searchParams, setSearchParams] = useSearchParams();

  const selectIcon = (
    <IconCaretDownFilled width={12} color="rgba(112,112,112,0.4)" />
  );

  // fetch all user found data loaded
  const [dataLoadedUserFound, setDataLoadedUserFound] = useState(false);

  const [useLabelAsValue, setUseLabelAsValue] = useState(false);
  useEffect(() => {
    if (Array.isArray(inputValues?.collection_data)) {
      const selectOptionsData = inputValues.collection_data
        .filter(
          (item) =>
            (item.collection_name &&
              typeof item.collection_name === "string") ||
            (item.collection_hash && typeof item.collection_hash === "string"),
        )
        .map((item) => {
          const collectionName =
            item.collection_name === "n/a" && item.collection_hash === "n/a"
              ? "<<None>>"
              : item.collection_name === "n/a" && item.collection_hash !== "n/a"
              ? "<<Not Available>>"
              : item.collection_name;
          const collectionHash =
            item.collection_hash === "n/a" ? "<<None>>" : item.collection_hash;

          const label = collectionName
            ? formatCollectionUserFound(collectionName).formatted
            : formatHexStr(collectionHash, 6, 4, 13);

          return {
            label: label,
            value: useLabelAsValue ? label : collectionHash,
          };
        });

      setSelectOptions(selectOptionsData);
    }
  }, [inputValues?.collection_data, dataLoadedUserFound, useLabelAsValue]);

  const [selectOptions, setSelectOptions] = useState([]);

  // Initial state for input values
  const [inputValues, setInputValues] = useState({
    collection_data: [],
    commitment_receipts_data: [],
    user_info_data: {},
    user_found: false,
    user_logged_in: true,
  });

  const [collectionExists, setCollectionExists] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const user = params.get("user");

    const collectionName = params.get("collection_name");
    const collectionCid = params.get("collection_cid");
    const userAddress = params.get("user_address");
    const persistentId = params.get("persistent_id");
    const userName = params.get("user_name");

    if (collectionName || collectionCid) {
      setCollectionExists(true);
    }
    const fetchUserFoundData = async () => {
      await userFoundData(
        setInputValues,
        user,
        setLoading,
        userAddress,
        persistentId,
        userName,
        collectionName,
        collectionCid,
      );
      setDataLoadedUserFound(true);
    };
    fetchUserFoundData();

    // set collection filtering from URL
    const collectionFromUrl = searchParams.get("collection_id");
    if (collectionFromUrl) {
      setSearchTerm(collectionFromUrl);
      setActiveTab("stamp_history");
    }
  }, []);

  const [searchTerm, setSearchTerm] = useState("");

  const handleSelectChange = (value) => {
    if (value === null) {
      setSearchTerm("");
    } else {
      setSearchTerm(value);
    }
  };

  const handleSearchChange = (value) => {
    if (value === null) {
      setSearchTerm("");
    } else {
      setSearchTerm(value);
      setUseLabelAsValue(true);
    }
  };

  const [filteredItemsNoPagination, setFilteredItemsNoPagination] = useState(
    [],
  );

  const handleFilteredItemsNoPaginationChange = useCallback((items) => {
    setFilteredItemsNoPagination(items);
  }, []);

  const handleClickFilterredbyCollection = (
    collection_name,
    collection_hash,
  ) => {
    setUseLabelAsValue(true);
    setActiveTab("stamp_history");
    setSearchTerm(collection_name);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("collection_id", collection_hash);
      return newParams;
    });
  };

  // Initialize the activeTab state from the URL hash
  const [activeTab, setActiveTab] = useState(() => {
    // Extract the hash from the URL (without the '#' symbol)
    const hash = window.location.hash.slice(1);
    return hash || "stamp_history"; // Default to 'Stamp History' tab if no hash is present
  });

  useEffect(() => {
    // Handler for hash change events
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash) {
        setActiveTab(hash); // Update the active tab based on the hash
      }
    };

    // Listen for changes to the URL hash
    window.addEventListener("hashchange", handleHashChange);

    // Clean up the event listener when the component is unmounted
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Function to handle tab change
  const handleTabChange = (value) => {
    setActiveTab(value); // Update the active tab
    window.location.hash = value; // Update the URL hash when the tab changes
  };

  const [loading, setLoading] = useState(true);
  const userTimezone = inputValues?.user_info_data?.display_timezone;

  // Icons used within the tables, provided through a custom utility function
  const faIcons = useMemo(() => useTableIcons(), []);

  // Data preparation for the main table using memoized custom utility function
  const dataCollections = useMemo(
    () =>
      collectionsTableData(
        inputValues["collection_data"],
        staticBasePath,
        userTimezone,
        handleClickFilterredbyCollection,
        theme,
        iconsClasses,
      ),
    [filteredItemsNoPagination, staticBasePath, userTimezone],
  );

  // Column definitions for the main table using a utility function
  const columnsCollections = useMemo(() => collectionsTableColumns(), []);

  // Configuration options for the main table using Mantine's table library
  const tableCollections = useMantineReactTable(
    collectionsTableOptions(
      columnsCollections,
      dataCollections,
      faIcons,
      theme,
    ),
  );

  // Data preparation for the main table using memoized custom utility function
  const dataStampHistory = useMemo(
    () =>
      stampHistoryTableData(
        filteredItemsNoPagination,
        staticBasePath,
        userTimezone,
        handleClickFilterredbyCollection,
        theme,
        iconsClasses,
      ),
    [filteredItemsNoPagination, staticBasePath, userTimezone],
  );

  // Column definitions for the main table using a utility function
  const columnsStampHistory = useMemo(
    () => stampHistoryTableColumns(userTimezone),
    [userTimezone],
  );

  // Configuration options for the main table using Mantine's table library
  const tableStampHistory = useMantineReactTable(
    stampHistoryTableOptions(
      columnsStampHistory,
      dataStampHistory,
      faIcons,
      theme,
    ),
  );

  // Data preparation for the main table using memoized custom utility function
  const dataCollection = useMemo(
    () =>
      collectionTableData(
        filteredItemsNoPagination,
        staticBasePath,
        userTimezone,
        theme,
        iconsClasses,
      ),
    [filteredItemsNoPagination, staticBasePath, userTimezone],
  );

  // Column definitions for the main table using a utility function
  const columnsCollection = useMemo(
    () => collectionTableColumns(theme, userTimezone),
    [theme, userTimezone],
  );

  // Configuration options for the main table using Mantine's table library
  const tableCollection = useMantineReactTable(
    collectionTableOptions(columnsCollection, dataCollection, faIcons, theme),
  );

  const items = inputValues["commitment_receipts_data"];
  // Filtered items based on the search term
  const filtered = useMemo(
    () =>
      Array.isArray(items) && items.length > 0
        ? items.filter((item) => {
            const nameOrHash =
              item.collection_name && item.collection_name !== "n/a"
                ? item.collection_name
                : item.collection_hash && item.collection_hash !== "n/a"
                ? formatHexStr(item.collection_hash, 6, 4, 13)
                : "<<None>>";

            return (
              nameOrHash &&
              typeof nameOrHash === "string" &&
              formatCollectionUserFound(nameOrHash)
                .formatted.toLowerCase()
                .includes(searchTerm.toLowerCase())
            );
          })
        : [],
    [items, searchTerm],
  );

  useEffect(() => {
    handleFilteredItemsNoPaginationChange(filtered);
  }, [filtered, handleFilteredItemsNoPaginationChange]);

  const getTitleText = () => {
    const params = new URLSearchParams(window.location.search);
    const hasValidParam =
      params.has("user_name") ||
      params.has("user_address") ||
      params.has("persistent_id") ||
      params.has("user");

    if (!hasValidParam) {
      return "Invalid argument, please specify a valid user";
    }

    if (inputValues?.user_info_data?.user_address) {
      if (collectionExists) {
        return inputValues?.collection_data.length > 0
          ? "Collection Detail"
          : "Collection Not Found";
      }
      return "User Found!";
    }

    return "User Not Found!";
  };

  return (
    <Box className="container">
      {loading && (
        <Box className="user-found-loader-wrap">
          <Loading
            title="Please wait, retrieving data"
            text="Can take 10 seconds, but usually faster!"
          />
        </Box>
      )}
      {!loading && (
        <Box pb={theme.padding.lgxEm}>
          {(inputValues?.user_info_data?.user_address && !collectionExists) ||
          (collectionExists && inputValues?.collection_data?.length > 0) ? (
            <Image
              src={`${staticBasePath}public/images/svg/vb-icon-verified.svg`}
              w={64}
              alt="User Found!"
              className={iconsClasses.title}
            />
          ) : (
            <Image
              src={`${staticBasePath}public/images/svg/vb-icon-not-verified.svg`}
              w={64}
              alt="User Not Found!"
              className={iconsClasses.title}
            />
          )}
          <Flex
            gap={theme.flex.gap.xs}
            align={theme.flex.align.center}
            justify={theme.flex.justify.center}
          >
            <Title
              classNames={{
                root: h1Classes.h1,
              }}
              order={1}
            >
              {getTitleText()}
            </Title>
          </Flex>
        </Box>
      )}
      {inputValues?.user_info_data?.user_address ? (
        <Box className={contentBlocksClasses.white}>
          <Flex
            className={userContentClasses.blockFlex}
            justify={theme.flex.justify.between}
            wrap="wrap"
            pb={theme.padding.xxllEm}
          >
            <Box className="block-flex-left">
              {inputValues?.user_info_data?.user_name && (
                <Box>
                  <Text
                    classNames={() => ({
                      root: userFoundClasses.textKey,
                    })}
                    component="span"
                  >
                    Username:{" "}
                  </Text>
                  <Text
                    classNames={() => ({
                      root: userFoundClasses.textValue,
                    })}
                    component="span"
                  >
                    {collectionExists ? (
                      <CustomAnchor
                        text={inputValues?.user_info_data?.user_name}
                        componentClasses="link"
                        color={theme.customColors.black}
                        href={`/verify/user-data/?user=${inputValues?.user_info_data?.user_name}`}
                      ></CustomAnchor>
                    ) : (
                      inputValues?.user_info_data?.user_name
                    )}
                  </Text>
                </Box>
              )}

              {inputValues?.user_info_data?.user_persistent_id && (
                <Box>
                  <Text
                    classNames={() => ({
                      root: userFoundClasses.textKey,
                    })}
                    component="span"
                  >
                    Persistent User ID:{" "}
                  </Text>
                  <Text
                    classNames={() => ({
                      root: userFoundClasses.textValue,
                    })}
                    component="span"
                  >
                    {collectionExists ? (
                      <CustomAnchor
                        text={inputValues?.user_info_data?.user_persistent_id}
                        componentClasses="link"
                        color={theme.customColors.black}
                        href={`/verify/user-data/?user=${inputValues?.user_info_data?.user_persistent_id}`}
                      ></CustomAnchor>
                    ) : (
                      inputValues?.user_info_data?.user_persistent_id
                    )}
                  </Text>
                </Box>
              )}
              {inputValues?.user_info_data?.user_address && (
                <Flex wrap="wrap" gap={theme.flex.gap.xxs}>
                  <Text
                    classNames={() => ({
                      root: userFoundClasses.textKey,
                    })}
                    component="span"
                  >
                    User Blockchain Address:{" "}
                  </Text>
                  <Text
                    classNames={() => ({
                      root:
                        userFoundClasses.textValue +
                        " " +
                        userFoundClasses.textValueBlockChainAddressFix,
                    })}
                    component="span"
                  >
                    {inputValues?.user_info_data?.user_address}
                  </Text>
                </Flex>
              )}
              <Flex
                gap={theme.flex.gap.xs}
                pb={theme.padding.baseEm}
                wrap="wrap"
              >
                <Text
                  classNames={() => ({
                    root: userFoundClasses.textKey,
                  })}
                  component="span"
                >
                  Current Identity Status?{" "}
                </Text>
                {inputValues?.user_info_data?.is_verified ? (
                  <>
                    {" "}
                    <Image
                      src={`${staticBasePath}public/images/svg/verified.svg`}
                      alt="verified"
                      className={iconsClasses.userVerified}
                    />
                    <Text
                      classNames={() => ({
                        root: userFoundClasses.textValue,
                      })}
                      component="span"
                    >
                      {" "}
                      Verified{" "}
                      {inputValues?.user_info_data?.verified_since && (
                        <Text component="span">
                          (since{" "}
                          {formatTimeStamp(
                            inputValues?.user_info_data?.verified_since,
                            inputValues?.user_info_data?.display_timezone,
                            SHORT_DISPLAY_FORMAT,
                          )}
                          )
                        </Text>
                      )}
                    </Text>
                  </>
                ) : (
                  <Text
                    classNames={() => ({
                      root: userFoundClasses.textValue,
                    })}
                    component="span"
                  >
                    {" "}
                    Not Verified
                  </Text>
                )}
              </Flex>

              {collectionExists && (
                <>
                  {inputValues?.collection_data?.[0]?.collection_name && (
                    <Box>
                      <Text
                        classNames={() => ({
                          root: userFoundClasses.textKey,
                        })}
                        component="span"
                      >
                        Collection Name:{" "}
                      </Text>
                      <Text
                        classNames={() => ({
                          root: userFoundClasses.textValue,
                        })}
                        component="span"
                      >
                        {inputValues?.collection_data?.[0]?.collection_name}
                      </Text>
                    </Box>
                  )}

                  {inputValues?.collection_data?.[0]?.collection_hash && (
                    <Flex wrap="wrap" gap={theme.flex.gap.xxs}>
                      <Text
                        classNames={() => ({
                          root: userFoundClasses.textKey,
                        })}
                        component="span"
                      >
                        Collection ID:{" "}
                      </Text>
                      <Text
                        classNames={() => ({
                          root:
                            userFoundClasses.textValue +
                            " " +
                            userFoundClasses.textValueBlockChainAddressFix,
                        })}
                        component="span"
                      >
                        {inputValues?.collection_data?.[0]?.collection_hash}
                      </Text>
                    </Flex>
                  )}
                </>
              )}

              {(collectionExists
                ? inputValues?.collection_data?.[0]?.collection_description
                : inputValues?.user_info_data?.description) && (
                <Box>
                  <Text
                    classNames={() => ({
                      root: userFoundClasses.textKey,
                    })}
                    component="span"
                  >
                    About {collectionExists && "Collection"}:{" "}
                  </Text>

                  {collectionExists &&
                  inputValues?.collection_data?.length > 0 ? (
                    <Box
                      className={userContentClasses.userContent}
                      dangerouslySetInnerHTML={{
                        __html: decodeHTML(
                          inputValues?.collection_data?.["0"]
                            .collection_description,
                        ),
                      }}
                    />
                  ) : (
                    <Box
                      className={userContentClasses.userContent}
                      dangerouslySetInnerHTML={{
                        __html: decodeHTML(
                          inputValues?.user_info_data?.description,
                        ),
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
            <Box className="block-flex-right">
              {inputValues?.user_info_data?.total_stamps > 0 && (
                <>
                  <Box
                    bg={theme.customBackgrounds.infoBlock}
                    p={theme.padding.smMdEm}
                    mb={theme.margin.xsEm}
                  >
                    <Text
                      styles={() => ({
                        root: {
                          textAlign: "center",
                        },
                      })}
                    >
                      Total Stamps
                    </Text>
                    <Text
                      styles={(theme) => ({
                        root: {
                          textAlign: "center",
                          fontFamily: theme.headings.fontFamily.medium,
                          fontSize: "1.5em",
                          lineHeight: "1em",
                        },
                      })}
                    >
                      {inputValues?.user_info_data?.total_stamps}
                    </Text>
                  </Box>
                  {collectionExists && inputValues?.collection_data && (
                    <>
                      <Box
                        bg={theme.customBackgrounds.infoBlock2}
                        p="0.7em"
                        mb={theme.margin.xsEm}
                      >
                        <Text
                          styles={() => ({
                            root: {
                              textAlign: "center",
                              color: theme.customColors.white,
                            },
                          })}
                        >
                          First Stamp
                        </Text>
                        <Text
                          styles={(theme) => ({
                            root: {
                              textAlign: "center",
                              fontFamily: theme.headings.fontFamily.medium,
                              fontSize: "1.2em",
                              lineHeight: "1em",
                              color: theme.customColors.white,
                            },
                          })}
                        >
                          {formatTimeStamp(
                            inputValues?.collection_data["0"].first_timestamp,
                            userTimezone,
                            SHORT_DISPLAY_FORMAT,
                          )}
                        </Text>
                      </Box>

                      <Box
                        bg={theme.customBackgrounds.infoBlock}
                        p={theme.padding.smMdEm}
                        mb={theme.margin.xsEm}
                      >
                        <Text
                          styles={() => ({
                            root: {
                              textAlign: "center",
                            },
                          })}
                        >
                          Last Stamp
                        </Text>
                        <Text
                          styles={(theme) => ({
                            root: {
                              textAlign: "center",
                              fontFamily: theme.headings.fontFamily.medium,
                              fontSize: "1.2em",
                              lineHeight: "1em",
                            },
                          })}
                        >
                          {formatTimeStamp(
                            inputValues?.collection_data["0"].last_timestamp,
                            userTimezone,
                            SHORT_DISPLAY_FORMAT,
                          )}
                        </Text>
                      </Box>
                    </>
                  )}
                </>
              )}
              {inputValues?.user_info_data?.total_collections > 0 &&
                !collectionExists && (
                  <Box
                    bg={theme.customBackgrounds.infoBlock2}
                    p="0.7em"
                    mb={theme.margin.xsEm}
                  >
                    <Text
                      styles={() => ({
                        root: {
                          textAlign: "center",
                          color: theme.customColors.white,
                        },
                      })}
                    >
                      Collections
                    </Text>
                    <Text
                      styles={(theme) => ({
                        root: {
                          textAlign: "center",
                          fontFamily: theme.headings.fontFamily.medium,
                          fontSize: "1.5em",
                          lineHeight: "1em",
                          color: theme.customColors.white,
                        },
                      })}
                    >
                      {inputValues?.user_info_data?.total_collections}
                    </Text>
                  </Box>
                )}
            </Box>
          </Flex>
          {collectionExists ? (
            <Box className={tableClasses.table}>
              <MantineReactTable
                data={collectionTableData}
                columns={collectionTableColumns}
                table={tableCollection}
              />
            </Box>
          ) : (
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Flex justify={theme.flex.justify.between} wrap="wrap">
                <Tabs.List className={tabsTableClasses.tabsList}>
                  <Tabs.Tab
                    className={tabsTableClasses.tab}
                    value="collections"
                  >
                    Collections
                  </Tabs.Tab>
                  <Tabs.Tab
                    className={tabsTableClasses.tab}
                    value="stamp_history"
                  >
                    Stamp History
                  </Tabs.Tab>
                </Tabs.List>

                <Flex
                  align={theme.flex.align.center}
                  wrap="wrap"
                  className={tabsTableClasses.selectWrap}
                >
                  {activeTab === "stamp_history" &&
                    filteredItemsNoPagination.length > 0 && (
                      <Box>
                        <DownloadCSV
                          data={filteredItemsNoPagination}
                          displayName={inputValues?.user_info_data?.user_name}
                          userAddress={
                            inputValues?.user_info_data?.user_address
                          }
                        />
                      </Box>
                    )}
                  {activeTab === "stamp_history" && selectOptions.length > 0 && (
                    <Select
                      pb={theme.padding.xsm}
                      className={tabsTableClasses.select}
                      label=""
                      placeholder="Filter by Collection"
                      data={selectOptions}
                      rightSection={selectIcon}
                      filter={(data) =>
                        optionsFilter({
                          options: data.options,
                          search: data.search,
                        })
                      }
                      searchable
                      value={searchTerm}
                      onChange={(value) => handleSelectChange(value)}
                      onSearchChange={(value) => handleSearchChange(value)}
                    />
                  )}
                </Flex>
              </Flex>

              <Tabs.Panel value="collections">
                <Box className={tableClasses.table}>
                  <MantineReactTable
                    data={collectionsTableData}
                    columns={collectionsTableColumns}
                    table={tableCollections}
                  />
                </Box>
              </Tabs.Panel>

              <Tabs.Panel value="stamp_history">
                <Box className={tableClasses.table}>
                  <MantineReactTable
                    data={stampHistoryTableData}
                    columns={stampHistoryTableColumns}
                    table={tableStampHistory}
                  />
                </Box>
              </Tabs.Panel>
            </Tabs>
          )}
        </Box>
      ) : null}
      {!collectionExists && !loading && (
        <Box mt={theme.margin.mdSpacious}>
          <CustomAnchor
            text="Back to Verify"
            componentClasses="buttonBlack anchor"
            href={URLs.VERIFY_URL}
            IconChevronLeftEnable={true}
          ></CustomAnchor>
        </Box>
      )}
    </Box>
  );
};

UserFoundContent.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
};
export default UserFoundContent;
